import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../icon/lib-icon';
import css from './lib-accordion.css';

const chevron = new URL('../wago-icons-svg/btn-chevron-down.svg', import.meta.url).href;
const spinner = new URL('../wago-icons-svg/spinner.svg', import.meta.url).href;

/** A disclosure with independent header actions and an intrinsically sized body. */
@customElement('lib-accordion')
export class LibAccordion extends LibElement {
  protected override get hostFocusSelector(): string { return '#toggle'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];

  @property() label = '';
  @property({ type: Boolean, reflect: true }) expanded = false;
  @property({ type: Boolean }) busy = false;
  @property({ type: Boolean }) managed = false;
  @property() surface = 'white';
  @property({ attribute: 'badge-number', type: Number }) badgeNumber?: number;
  @state() private animating = false;
  #timer?: ReturnType<typeof setTimeout>;

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (this.hasUpdated && changed.has('expanded')) {
      this.animating = true;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => { this.animating = false; }, 450);
    }
  }

  override disconnectedCallback(): void {
    clearTimeout(this.#timer);
    this.animating = false;
    super.disconnectedCallback();
  }

  #toggle(): void {
    if (this.busy) return;
    if (!this.managed) this.expanded = !this.expanded;
    this.emit('libaccordiontoggle');
  }

  #transitionEnd(event: TransitionEvent): void {
    if (event.target === event.currentTarget && event.propertyName === 'grid-template-rows') {
      clearTimeout(this.#timer);
      this.animating = false;
    }
  }

  override render() {
    const badgeText = (this.badgeNumber ?? 0) > 9 ? '9+' : this.badgeNumber;
    return html`<div class="lib-accordion ${this.expanded ? 'lib-accordion-expanded' : ''} ${this.animating ? 'lib-accordion-animating' : ''}">
      <div class="lib-accordion-header">
        <button type="button" id="toggle" class="lib-accordion-toggle" aria-expanded=${String(this.expanded)}
          aria-controls="panel" aria-busy=${this.busy ? 'true' : nothing} @click=${this.#toggle}>
          <slot name="icon"></slot><span class="lib-accordion-label">${this.label}</span>
          ${this.badgeNumber == null ? nothing : html`<span class="lib-accordion-badge">${badgeText}</span>`}
        </button>
        <slot name="header-actions"></slot>
        <span class="lib-accordion-chevron" aria-hidden="true">
          <lib-icon class="lib-accordion-chevron-icon" icon-url=${this.busy ? spinner : chevron} ?spinning=${this.busy}></lib-icon>
        </span>
      </div>
      <div id="panel" class="lib-accordion-disclosure" role="region" aria-labelledby="toggle"
        aria-hidden=${this.expanded ? nothing : 'true'} ?inert=${!this.expanded} @transitionend=${this.#transitionEnd}>
        <div class="lib-accordion-panel"><slot name="body"></slot><slot name="action"></slot></div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-accordion': LibAccordion; }
}
